import React, { forwardRef } from 'react'

import { OmwSpinner } from 'components/ui'

import i18n from 'i18n'
import classNames from 'classnames'
import { ClientJS } from 'clientjslite'

import EmailSubject from 'emailThreadViewer/EmailView/EmailSubject'
import EmailView from 'emailThreadViewer/EmailView'
import type { PrintPayload } from 'emailThreadViewer/EmailView/printPayload'
import type { ReadReceiptInfo, ViewerWindow } from 'emailThreadViewer/ViewerIFace'
import type { EmailContainerApi, WebToNativeApi } from 'components/viewer'
import emptyEmail from 'assets/images/icon-empty-email.svg'
// import PerfectScrollbar from 'libs/react-perfect-scrollbar'

import { isIframeMode } from '../helper/iframeMode'

import { scrollIntoViewIfNeeded } from 'utils/core'
import { creactReactObjMgr } from 'utils/ui/core'

export interface ReactObjMgr {
  reactObjList: any[]
  updateUI: () => void
  forceUpdate: void
  run: (runFunc: (reactObj: any) => void) => void
  addReactObj: (reactObj: any) => void
  removeReactObj: (reactObj: any) => void
}

import IFace, { initIFace } from '../ViewerIFace'

import styles from './style.module.scss'
import { TextFinderProvider } from 'emailThreadViewer/TextFinder'
import findTextObserver from 'emailThreadViewer/TextFinder/utils/findTextObserver'

interface MailInfo {
  id: string
  messageId: string
  subject?: string
  read?: boolean
  draft?: boolean
  from?: {
    email: string
    name?: string
  }
  body?: any
  ai_extraction?: any
  received_time?: number
  mailbox_name?: string
  mailbox_fullname?: string
  [key: string]: any
}

interface EmailData {
  emailId: string
  messageId: string
  fold: boolean
  hidden: boolean
  mailInfo: MailInfo
  reqBody: () => void
  setFlagged: (value: boolean) => Promise<any>
  setRead: (value: boolean) => Promise<any>
  reactMgr: ReactObjMgr
  reactObj?: any
}

interface MessageMap {
  [messageId: string]: {
    emails: EmailData[]
    curId: string
  }
}

interface ThreadInfo {
  important?: boolean
  labels?: Array<{
    id: string
    name: string
    text_color?: string
    background_color?: string
  }>
  [key: string]: any
}

interface EmailContainerState {
  emailList: EmailData[]
  loading: boolean
  threadInfo: ThreadInfo | null
  foldThread: boolean | null
  scrollVisible: boolean
  hideEmptyIcon: boolean
}

// props 가 없음 — 모든 상태는 EmailContainerState 로 자체 관리. ViewerIFace 가
// ref 로 직접 메서드를 호출 (initEmail / applyEmail / applyThread 등) 하는 패턴.
// class prop 타입 — 평문 string 또는 classNames() 반환값(string)을 받는다. host 가 주입한다.
type ClassNameValue = string | ReturnType<typeof classNames>

interface EmailContainerProps {
  // 마운트 완료(componentDidMount 마지막) 시 viewer window 를 넘겨 호출된다(선택).
  onMount?: (viewerWindow: ViewerWindow) => void
  // 뷰어 최상위 `.emailContainer` 엘리먼트에 덧붙일 class — host 가 주입한다(예: 다크모드 'om-light-island').
  containerClass?: ClassNameValue
  globalDarkMode?: boolean
  darkModeTargetSelector?: string[] | null
  darkMode?: boolean
  darkmodeBackcolor?: string | null
}

export class EmailContainer
  extends React.Component<EmailContainerProps, EmailContainerState>
  implements EmailContainerApi
{
  state: EmailContainerState = {
    emailList: [],
    loading: false,
    threadInfo: null,
    foldThread: null,
    scrollVisible: false,
    hideEmptyIcon: false,
  }

  emailList: EmailData[] = []

  messageMap: MessageMap = {}

  emailIdMap: Record<string, MessageMap[string]> = {}

  selectedEmailId: string | null = null

  aiExtractionEmailId: string | null = null
  aiExtractionInitialOpen?: boolean

  // setThread시에 aiExtractionResetToken을 증가되게 하여 aiExtraction이 초기화 되도록 함.
  aiExtractionResetToken: number = 0
  clearTime: number = 0

  loading: boolean = false

  subjectRef = React.createRef<any>()

  // inner mode 뷰어 루트(`.emailContainer` elem). DocUtil rootContainer 주입원(§15.14-⑥).
  // ref 콜백으로 채워지며, initIFace 가 getRootContainer 로 읽어 initMailView 생성 시점에 전달한다.
  rootContainerElem: HTMLElement | null = null

  scrollVisibleTimer: NodeJS.Timeout | null = null

  extObj?: WebToNativeApi

  // initIFace 가 host window 에 등록한 webObj/bridge 를 언마운트 시 해제하기 위한 disposer.
  disposeIFace?: () => void

  // scrollRef = React.createRef();

  // initIFace 가 DocUtil 생성 시점(initMailView)에 읽어 rootContainer 로 주입한다.
  getRootContainer = (): HTMLElement | null => this.rootContainerElem

  // `.emailContainer` div 의 callback ref. emailList 가 비면 div 자체가 없어 null 이 들어오고,
  // 첫 메일 로드/재마운트로 마운트되면 elem 이 들어온다. DocUtil 이 이미 존재하면(초기 emptyList 이후
  // populate, 또는 재마운트) setRootContainer 로 즉시 재주입한다(§15.14-⑥). DocUtil 생성 전(최초 마운트)
  // 이면 rootContainerElem 만 채워지고, 곧 componentDidMount→initIFace→initMailView 가 이 값을 읽어 간다.
  setRootContainerRef = (elem: HTMLElement | null): void => {
    this.rootContainerElem = elem
    ;(
      window as { docUtil?: { setRootContainer?: (el: HTMLElement | null) => void } }
    ).docUtil?.setRootContainer?.(elem)
  }

  componentDidMount(): void {
    const { darkMode, darkmodeBackcolor, globalDarkMode, darkModeTargetSelector } = this.props
    this.disposeIFace = initIFace(
      window,
      this,
      darkMode,
      darkmodeBackcolor,
      globalDarkMode,
      darkModeTargetSelector,
    )

    const client = new ClientJS()
    const browser = client.getBrowser()

    document.body.setAttribute('data-browser', browser.toLowerCase())

    i18n.on('languageChanged', this.onLanguageChanged)

    // §2c.4 — inner mode 에서는 blurHandler 미등록. inner mode 에서 메일 본문 focus
    // 이동 시 host 의 om-dropdown 이 강제 close 되는 회귀 (R13) 회피. iframe 모드만 등록.
    if (isIframeMode()) {
      window.addEventListener('blur', this.blurHandler)
    }

    // 마운트 완료 후 host 에 viewer window 를 전달(선택). initIFace 로 webObj/NineNative 등록이
    // 끝난 뒤 호출되도록 componentDidMount 마지막에 둔다.
    this.props.onMount?.(window)
  }

  componentDidUpdate(prevProps: Readonly<EmailContainerProps>) {
    const { darkMode, darkmodeBackcolor } = this.props

    if (prevProps != null && prevProps.darkMode !== darkMode) {
      ;(
        window as { docUtil?: { setDarkMode?: (isDarkMode: boolean) => void } }
      ).docUtil?.setDarkMode?.(darkMode === true)
    } else if (prevProps != null && prevProps.darkmodeBackcolor !== this.props.darkmodeBackcolor) {
      ;(
        window as { docUtil?: { setDarkBackColor?: (color: string) => void } }
      ).docUtil?.setDarkBackColor?.(darkmodeBackcolor ?? '#000')
    }
  }

  componentWillUnmount(): void {
    i18n.off('languageChanged', this.onLanguageChanged)

    // §2c.4 — 등록 조건과 동일한 가드. inner mode 에서는 등록하지 않았으므로 제거도 skip.
    if (isIframeMode()) {
      window.removeEventListener('blur', this.blurHandler)
    }

    if (this.scrollVisibleTimer != null) {
      clearTimeout(this.scrollVisibleTimer)
      this.scrollVisibleTimer = null
    }

    // unmount 시에도 docUtil.mailViewMap에 등록된 본문 element를 해제해
    // 전역 맵이 detached node를 붙잡지 않도록 한다. (uiUpdate=false — unmount 중 forceUpdate 방지)
    this.clearAllEmail(false)

    // InnerViewer 모드에서 host window.webObj 가 이 인스턴스를 영구 보존하는 누수 차단.
    // (iframe 모드는 about:blank 로 contentWindow 와 함께 사라지므로 무해)
    if (this.disposeIFace != null) {
      this.disposeIFace()
      this.disposeIFace = undefined
    }
    this.extObj = undefined
  }

  blurHandler = (e: FocusEvent): void => {
    console.log(' -- blurHandler : ', e)

    // iframe 내부를 클릭하면 menu가 닫히지 않은 상태로 남아있으므로, 강제로 닫아줌.
    const openedElems = window.document.querySelectorAll('.om-dropdown-open')
    openedElems.forEach((elem) => {
      ;(elem as HTMLElement).click()
    })

    const openedSubmenuElems = window.document.querySelectorAll('.om-menu-submenu-open')
    openedSubmenuElems.forEach((elem) => {
      if (elem.children.length > 0) {
        ;(elem.children[0] as HTMLElement).click()
      } else {
        ;(elem as HTMLElement).click()
      }
    })
  }

  onLanguageChanged = (): void => {
    this.forceUpdate()
  }

  setLoading = (value: boolean): void => {
    console.log(' --- setLoading : ', value)
    if (this.loading !== value) {
      this.loading = value
      this.setState({
        loading: this.loading,
      })
    }
  }

  getEmailIds = (): string[] => {
    const { emailList } = this
    const emailIds = emailList.map((emailData) => {
      return emailData.emailId
    })

    return emailIds
  }

  setSecureSendInfo = (
    emailId: string,
    data: { isSecureMail: boolean; expiredDate: string | null; secureMailId: string | null },
  ): void => {
    const { emailList } = this

    const existMsg = emailList.find((emailData) => {
      return emailData.emailId === emailId
    })

    if (existMsg != null) {
      const { reactObj } = existMsg

      if (reactObj != null && reactObj.onSetSecureSendInfo != null) {
        reactObj.onSetSecureSendInfo(data)
      } else {
        existMsg.mailInfo.secureSendInfo = data
      }
    }
  }

  setReadReceiptInfo = (
    emailId: string,
    isReadReceipt: boolean,
    data: ReadReceiptInfo | null,
  ): void => {
    const { emailList } = this

    const existMsg = emailList.find((emailData) => {
      return emailData.emailId === emailId
    })

    if (existMsg != null) {
      const { reactObj } = existMsg

      if (reactObj != null && reactObj.onSetReadReceiptInfo != null) {
        reactObj.onSetReadReceiptInfo(isReadReceipt, data)
      } else {
        existMsg.mailInfo.readReceiptInfo = data
        existMsg.mailInfo.isReadReceipt = isReadReceipt
      }
    }
  }

  setInlineImages = (emailId: string, inlineImages: any[]): void => {
    const { emailList } = this

    const existMsg = emailList.find((emailData) => {
      return emailData.emailId === emailId
    })

    if (existMsg != null) {
      const { reactObj } = existMsg

      if (reactObj != null && reactObj.onSetInlineImages != null) {
        reactObj.onSetInlineImages(inlineImages)
      }
    }
  }

  // OMD-194: host(NineNative.print)가 인쇄 payload 를 요청. emailId 로 EmailView 를 찾아
  // fold 펼침(async) 후 payload 를 콜백으로 반환한다. 대상이 없으면 null 콜백(silent no-op 금지).
  requestPrintPayload = (
    emailId: string,
    returnCallback: (payload: PrintPayload | null) => void,
  ): void => {
    const { emailList } = this

    const existMsg = emailList.find((emailData) => {
      return emailData.emailId === emailId
    })

    const reactObj = existMsg?.reactObj
    if (reactObj != null && reactObj.requestPrintPayload != null) {
      reactObj.requestPrintPayload(returnCallback)
    } else {
      returnCallback(null)
    }
  }

  setAiExtractionEmail = (
    aiExtractionEmailId: string,
    update: boolean,
    aiExtractionInitialOpen?: boolean,
  ): void => {
    this.aiExtractionEmailId = aiExtractionEmailId
    this.aiExtractionInitialOpen = aiExtractionInitialOpen
    this.aiExtractionResetToken += 1
    if (update === true) {
      this.forceUpdate()
    }
  }

  clearAllEmail = (uiUpdate: boolean = true): void => {
    this.clearTime = Date.now()
    this.messageMap = {}
    this.emailIdMap = {}
    this.selectedEmailId = null

    const docUtil = (
      window as {
        docUtil?: {
          removeViewElement: (id: string) => void
          clearAllViewElements?: () => void
        }
      }
    ).docUtil
    if (docUtil != null) {
      // mailViewList 누적 방지를 위해 일괄 정리 위임(폴백: 개별 removeViewElement).
      if (typeof docUtil.clearAllViewElements === 'function') {
        docUtil.clearAllViewElements()
      } else {
        this.emailList.forEach((emailData) => {
          docUtil.removeViewElement(emailData.emailId)
        })
      }
    }

    if (uiUpdate === true) {
      this.setEmailList([])
    } else {
      this.emailList = []
    }
  }

  setEmailList = (emailList: EmailData[], forced?: boolean): void => {
    // this.setState({
    //   emailList: [...this.emailList],
    // })
    let needUpdate = false
    if (this.emailList !== emailList) {
      this.emailList = emailList
      needUpdate = true
    }

    if (needUpdate === true || forced === true) {
      this.forceUpdate()
    }
  }

  addEmail = (
    emailId: string,
    mailInfo: MailInfo,
    afterEmailId?: string | null,
    refresh?: boolean,
    isSelected: boolean | null = null,
  ): void => {
    const { emailList } = this

    const refreshList = refresh != null ? refresh : true

    const existMsg = emailList.find((emailData) => {
      return emailData.emailId === emailId
    })

    if (existMsg == null) {
      const data: EmailData = {
        emailId: mailInfo.id,
        messageId: mailInfo.messageId,
        fold: isSelected != null ? !isSelected : emailList.length > 0,
        hidden: false,
        mailInfo,
        reqBody: () => {
          const getBody = IFace.WebToNative.getBody
          if (typeof getBody !== 'function') {
            // 브릿지 미초기화(initIFace 이전) 또는 해제(teardown 이후) 타이밍 레이스로
            // getBody 부재. reqDetails가 이미 isReqBody=true 래치를 세팅했으므로 그대로
            // skip하면 본문이 영구 미로드된다. reset()으로 래치를 롤백해, 인스턴스가
            // 아직 살아 있으면 이후 정상 트리거(fold 해제/forced) 시 재요청되게 한다.
            // (unmount 레이스면 reactObj가 이미 undefined라 no-op — crash만 방지.)
            data.reactObj?.reset?.()
            return
          }
          getBody(mailInfo.id).then((body: any) => {
            const { reactObj } = data
            if (body != null) {
              mailInfo.body = body
              if (reactObj != null && reactObj.onSetDetails != null) {
                reactObj.onSetDetails(body)
              }
            } else if (reactObj != null && reactObj.reset != null) {
              // unmount 후 reactObj가 해제된 상태에서 getBody가 늦게 resolve될 수 있어 가드.
              reactObj.reset()
            }
          })
        },
        setFlagged: (value: boolean) => {
          return IFace.WebToNative.setFlagged(mailInfo.id, value)
        },
        setRead: (value: boolean) => {
          return IFace.WebToNative.setRead(mailInfo.id, value, false)
        },
        reactMgr: creactReactObjMgr(),
      }

      if (this.messageMap[data.messageId] == null) {
        this.messageMap[data.messageId] = {
          emails: [data],
          curId: emailId,
        }
      } else {
        data.hidden = true
        this.messageMap[data.messageId].emails.push(data)
      }

      if ((emailList.length === 0 && isSelected == null) || isSelected === true) {
        this.selectedEmailId = emailId
      }

      this.emailIdMap[emailId] = this.messageMap[data.messageId]

      if (afterEmailId != null) {
        const idx = emailList.findIndex((emailData) => {
          return emailData.emailId === afterEmailId
        })

        if (idx >= 0) {
          emailList.splice(idx, 0, data)
        } else {
          emailList.push(data)
        }
      } else {
        emailList.push(data)
      }

      if (refreshList === true) {
        this.setEmailList(emailList, true)
      }
    }
  }

  deleteEmail = (emailId: string): void => {
    const { emailList } = this

    if ((window as { docUtil?: { removeViewElement: (id: string) => void } }).docUtil != null) {
      ;(
        window as { docUtil?: { removeViewElement: (id: string) => void } }
      ).docUtil.removeViewElement(emailId)
    }

    let emailIdx = -1
    const existItem = emailList.find((emailData, idx) => {
      if (emailData.emailId === emailId) {
        emailIdx = idx
        return true
      }
      return false
    })

    if (emailIdx >= 0) {
      emailList.splice(emailIdx, 1)

      const messageEmails = this.messageMap[existItem!.messageId].emails

      const idx = messageEmails.findIndex((emailData) => {
        return emailData.emailId === emailId
      })

      if (idx >= 0) {
        messageEmails.splice(idx, 1)
      }

      this.setEmailList(emailList, true)
    }
  }

  foldAll = (value: boolean): void => {
    const { emailList } = this

    emailList.forEach((emailData /*, idx */) => {
      const { reactObj } = emailData
      if (reactObj != null && reactObj.setFold != null) {
        emailData.fold = value
        reactObj.setFold(emailData.fold, false)
      }
    })

    this.checkFoldThread()
  }

  /**
   * 지정한 메일만 **읽음 처리 없이** 펼친다 — 검색 결과에서 연 스레드에서 검색어가 실제로 들어
   * 있는 형제 메일을 host 가 펼치는 용도(`NativeToWeb.expandEmails`).
   *
   * `select()` 를 쓰지 않는 이유: `select` 는 `setRead` 와 스크롤·현재 메일 교체까지 하는 "열람"
   * 이다. 여기서는 fold 만 바꾼다. 실제 EmailView 가 아직 없는 id(뷰어에 없는 서버 매치 id 등)와
   * 숨은(hidden) 복제 메일은 건너뛴다. 반환은 실제로 펼친 건수.
   */
  expandEmails = (emailIds: string[]): number => {
    const targets = new Set(emailIds)
    let expanded = 0

    this.emailList.forEach((emailData) => {
      if (!targets.has(emailData.emailId) || emailData.hidden === true) return
      if (emailData.fold !== true) return
      const { reactObj } = emailData
      if (reactObj == null || reactObj.setFold == null) return
      emailData.fold = false
      // notifyOwner=false — canonical `emailData.fold` 는 위에서 직접 갱신했고, 아래에서
      // `checkFoldThread` 를 한 번만 돌린다(메일마다 setState 하지 않는다).
      reactObj.setFold(false, false, { markRead: false })
      expanded += 1
    })

    if (expanded > 0) this.checkFoldThread()
    return expanded
  }

  select = (curEmailId: string, foldOhters: boolean, byViewer?: boolean): void => {
    console.log(' --- select : ', curEmailId)

    // const settings = getTempEnvParam('settings') || {}
    // const { useAiSummary } = settings

    const { emailList } = this
    const existEmailData = emailList.find((emailData) => {
      return emailData.emailId === curEmailId
    })

    if (existEmailData == null) {
      // this.setEmailList([])
      return
    }

    this.selectedEmailId = curEmailId

    if (this.emailIdMap[curEmailId] != null) {
      this.emailIdMap[curEmailId].curId = curEmailId
    }

    let isFirst = true

    emailList.forEach((emailData /* , idx */) => {
      const { emailId, messageId, mailInfo, reactObj } = emailData

      if (this.messageMap[messageId].emails.length > 1) {
        if (this.messageMap[messageId].curId === emailId) {
          // show.
          emailData.hidden = false
        } else {
          // hide.
          emailData.hidden = true
        }
      } else {
        emailData.hidden = false
      }

      const { hidden } = emailData

      if (hidden !== true) {
        if (emailId === curEmailId) {
          const { read } = mailInfo
          if (read !== true) {
            emailData.setRead(true)
          }
          if (byViewer !== true) {
            emailData.fold = false
            if (reactObj != null && reactObj.setFold != null) {
              reactObj.setFold(emailData.fold, false)
            }
          }
          if (
            isFirst === true &&
            this.subjectRef.current != null &&
            document.body.contains(this.subjectRef.current)
          ) {
            this.setScrollVisible(true)
            setTimeout(() => {
              const emailViewList = document.querySelector('.emailViewList')
              if (emailViewList != null && emailViewList.firstElementChild != null) {
                scrollIntoViewIfNeeded(emailViewList.firstElementChild as HTMLElement)
              }
              this.setScrollVisible(false)
            }, 100)
          } else {
            setTimeout(() => {
              if (reactObj != null && reactObj.scrollIntoView != null) {
                reactObj.scrollIntoView()
              }
            }, 100)
          }
        } else if (foldOhters === true) {
          emailData.fold = true
        }

        isFirst = false
      }

      if (foldOhters === true && emailId !== curEmailId) {
        if (reactObj != null && reactObj.setFold != null) {
          reactObj.setFold(emailData.fold, true)
        }
      }
    })

    this.setEmailList(emailList, true)

    this.checkFoldThread()
  }

  setSelectedInner = (curEmailId: string): void => {
    this.selectedEmailId = curEmailId

    this.forceUpdate()
  }

  setOptimizedBtn = (emailId: string, value: boolean): void => {
    const existEmailData = this.emailList.find((emailData) => {
      return emailData.emailId === emailId
    })

    if (existEmailData != null) {
      const { reactObj } = existEmailData
      if (reactObj != null && reactObj.enableExpandBtn != null) {
        reactObj.enableExpandBtn(value)
      }
    }
  }

  mergeObject = (originObj: any, changedData: any): boolean => {
    let isChanged = false
    Object.entries(changedData).forEach(([key, value]) => {
      const prev = originObj[key]
      if (prev == null || typeof prev !== 'object') {
        if (originObj[key] !== value) {
          originObj[key] = value
          isChanged = true
        }
      } else if (Array.isArray(prev) === true) {
        if (value == null || prev.length !== (value as unknown[]).length) {
          originObj[key] = value
          isChanged = true
        } else {
          prev.forEach((item: any, idx: number) => {
            if (this.mergeObject(item, (value as unknown[])[idx]) === true) {
              isChanged = true
            }
          })
        }
      } else {
        if (this.mergeObject(prev, value) === true) {
          isChanged = true
        }
      }
    })
    return isChanged
  }

  checkFoldThread = (): void => {
    const { emailList } = this

    const hasFoldItem = emailList
      .filter((item) => item.hidden !== true)
      .some((item) => item.fold === true)

    console.log(' --- checkFoldThread : ', hasFoldItem)

    this.setState({
      foldThread: hasFoldItem,
    })
  }

  setScrollVisible = (visible: boolean): void => {
    if (this.scrollVisibleTimer != null) {
      clearTimeout(this.scrollVisibleTimer)
      this.scrollVisibleTimer = null
    }

    if (visible === true) {
      this.setState({
        scrollVisible: true,
      })
    } else {
      this.scrollVisibleTimer = setTimeout(() => {
        this.scrollVisibleTimer = null
        this.setState({
          scrollVisible: false,
        })
      }, 1000)
    }
  }

  notifyEmailFoldChanged = (threadItem: EmailData, foldValue: boolean): void => {
    threadItem.fold = foldValue

    this.checkFoldThread()
  }

  notifyEmailChanged = (emailId: string, changedData: any): void => {
    const { emailList } = this

    const emailData = emailList.find((item) => {
      return item.mailInfo.id === emailId
    })

    if (emailData != null) {
      const { reactObj } = emailData

      // 아바타 직접 전달(host 계정 색 override 실시간 반영) — 발신자 email 이 그대로라 아래 from.email
      // 변경 기반 applySender 재요청에 안 걸리는 경우를 메운다. mergeObject 변경 여부와 무관하게
      // 즉시 반영해야 하므로 게이트 밖에서 처리한다.
      if (changedData.avatar != null && reactObj != null && reactObj.applyAvatar != null) {
        reactObj.applyAvatar(changedData.avatar)
      }

      const prevDraft = emailData.mailInfo.draft
      const prevFromEmail = emailData.mailInfo.from != null ? emailData.mailInfo.from.email : null
      // 2단계 로딩으로 subject가 뒤늦게 채워지는 경우를 잡기 위해 병합 전 값을 캡처한다.
      const prevSubject = emailData.mailInfo.subject
      if (this.mergeObject(emailData.mailInfo, changedData) === true && reactObj != null) {
        if (changedData.reset_body === true) {
          if (reactObj.reset != null) {
            reactObj.reset()
          }
        }

        if (prevDraft !== changedData.draft) {
          if (reactObj.onDraftChanged != null) {
            reactObj.onDraftChanged(changedData.draft)
          }
        }

        if (changedData.body != null) {
          if (reactObj.onSetDetails != null) {
            reactObj.onSetDetails(changedData.body)
          }
        }

        if (
          changedData.from != null &&
          (prevFromEmail == null || prevFromEmail !== changedData.from.email)
        ) {
          if (reactObj.applySender != null) {
            reactObj.applySender()
          }
        }

        emailData.reactMgr.updateUI()

        // subject가 undefined에서 채워지면(2단계 로딩 완료) EmailSubject는 title prop으로만
        // 값을 받으므로 컨테이너를 리렌더해야 한다. reactMgr.updateUI()는 EmailView만
        // 갱신하고 EmailSubject는 미등록이라 리렌더되지 않아 로딩 스핀이 잔류한다.
        if (
          prevSubject === undefined &&
          emailData.mailInfo.subject !== undefined &&
          emailList[0] === emailData
        ) {
          this.forceUpdate()
        }
      }
    }
  }

  notifyDownloadProgress = (emailId: string, attachmentId: string, progress: number): void => {
    console.log('--- notifyDownloadProgress : ', emailId, attachmentId, progress)
    const { emailList } = this

    const emailData = emailList.find((item) => {
      return item.mailInfo.id === emailId
    })

    if (emailData != null) {
      const { reactObj } = emailData
      if (reactObj != null) {
        reactObj.notifyDownloadProgress(attachmentId, progress)
      }
    }
  }

  notifyThreadInfoChanged = (changedData: ThreadInfo): void => {
    console.log('- notifyThreadInfoChanged : ', changedData)

    this.setState({
      threadInfo: changedData,
    })
  }

  /** @deprecated NativeToWeb 에서 사용하는 함수. window.TextFinder.setFindText 사용으로 변경됨에따라, 사용하지 않음 */
  setFindText = (text: string, options?: any): void => {
    findTextObserver.notify(text, options)
  }

  setHideEmptyIcon = (hideEmptyIcon: boolean): void => {
    this.setState({
      hideEmptyIcon: hideEmptyIcon,
    })
  }

  render(): React.ReactNode {
    const { loading, threadInfo, foldThread, scrollVisible, hideEmptyIcon } = this.state
    const { emailList, aiExtractionEmailId } = this
    const aiExtractionInitialOpen = this.aiExtractionInitialOpen

    const aiExtrEmail = emailList.find((emailData) => emailData.emailId === aiExtractionEmailId)

    const style: React.CSSProperties = {}
    if (IFace.getSettings != null) {
      const { defaultBgColor } = IFace.getSettings()
      if (defaultBgColor != null) {
        // React.CSSProperties 가 CSS custom property (`--xxx`) 를 명시 안 함 — index
        // signature 추가만으로 좁힘. 런타임 동일 객체.
        ;(style as React.CSSProperties & Record<`--${string}`, string>)['--main-bgcolor'] =
          defaultBgColor
      }
    }

    return (
      <OmwSpinner
        spinning={loading}
        size="large"
        wrapperClassName={classNames(styles.spinnerWrapper)}
      >
        {emailList.length > 0 ? (
          <div
            ref={this.setRootContainerRef}
            className={classNames(
              styles.emailContainer,
              'emailContainer',
              'etv-container',
              this.props.containerClass,
              {
                loading,
                scrollVisible,
              },
            )}
            style={style}
          >
            <TextFinderProvider key={emailList.map((emailData) => emailData.emailId).join(',')}>
              <div className={classNames(styles.subjectPortal, 'subjectPortal')}></div>
              <div className={classNames(styles.emailViewList, styles.scrollView, 'emailViewList')}>
                {aiExtrEmail && (
                  <EmailSubject
                    ref={this.subjectRef}
                    owner={this}
                    title={emailList[0].mailInfo.subject}
                    threadItem={aiExtrEmail}
                    threadInfo={threadInfo}
                    foldThread={foldThread}
                    aiExtractionInitialOpen={aiExtractionInitialOpen}
                    aiExtractionResetToken={this.aiExtractionResetToken}
                  />
                )}
                {emailList.map((emailData, idx) => {
                  const { fold, hidden, emailId } = emailData
                  const selected = this.selectedEmailId === emailId
                  const key = `${this.clearTime}_${emailData.emailId}`
                  const isFirst = idx === emailList.length - 1

                  return (
                    <EmailView
                      key={key}
                      owner={this}
                      selected={selected}
                      fold={fold}
                      hidden={hidden}
                      isFirst={isFirst}
                      threadItem={emailData as any}
                      hasTitle={!aiExtrEmail}
                    />
                  )
                })}
              </div>
            </TextFinderProvider>
          </div>
        ) : hideEmptyIcon === true ? null : (
          <EmptyEmail />
        )}
      </OmwSpinner>
    )
  }
}

const EmptyEmail: React.FC = () => {
  return (
    <div className={classNames(styles.emptyEmail, 'etv-container')}>
      <img src={emptyEmail} alt="emptyEmail" />
      <span>{i18n.t('mailbox.empty.emptyEmail')}</span>
    </div>
  )
}

export default forwardRef<any, EmailContainerProps>((props, ref) => {
  return <EmailContainer ref={ref} {...props} />
})
